import { Dispatch, SetStateAction } from "react";

import { ISelectDropdownOption } from "./formElements";
import { EtyStudent } from "./entities";
import { ICoverLetter, IResumeBuild } from "./services";

export interface IStudentData {
  id: number | null;
  applicant_id: { value: string | null; error: string };
  first_name: { value: string; error: string };
  middle_name: { value: string; error: string };
  last_name: { value: string; error: string };
  personal_email: { value: string; error: string };
  edu_email: { value: string; error: string };
  phone: { value: string; error: string };
  birth_date: { value: string; error: string };
};
export type TStudentField = 'applicant_id' | 'first_name' | 'middle_name' | 'last_name' | 'personal_email' | 'edu_email' | 'phone' | 'birth_date';

export interface IStudentDetailsFormProps {
  operation: string;
  isLoading: boolean;
  studentData: EtyStudent | null;
  formData: IStudentData;
  setFormData: Dispatch<SetStateAction<IStudentData>>;
  studentId: number | null;
}

// these field are fillable in form
export const studentFields = [
  "first_name",
  "applicant_id",
  "middle_name",
  "last_name",
  "personal_email",
  "edu_email",
  "phone",
  "birth_date",
] as const;

// Define the type for the payload
export type StudentPayload = {
  [key in StudentField]: string | ISelectDropdownOption | Date | null;
};

export interface UpsertStudentParams {
  payload: StudentPayload & { id: number | null; school_id: number }; // Ensure the payload has both StudentPayload and school_id
  operation: string;
  setIsProcessing: Dispatch<SetStateAction<boolean>>;
  successCallback?: () => void;
}

export interface DeleteStudentParams {
  payload: { id: number };
  setIsProcessing: Dispatch<SetStateAction<boolean>>;
  successCallback?: () => void;
}

export type StudentField = (typeof studentFields)[number];

export type StudentResumeTab = 'Builder' | 'Scanner';
export type StudentCoverLetterTab = 'Builder' | 'Scanner';
export type StudentInterviewsTab = 'Audio' | 'Video';

export type StudentTab = "Personal" | "Resume" | "Cover-Letter" | "Interviews" | "LinkedIn Optimization" | "Courses" | "Documents" | "Communications" | "Visa-Chances" | "Change-Log" | "Immigration" | "Notifications";
export type StudentImmigrationTab = "Passport" | "Permit" | "Visa" | "PR Options";
export type StudentNotificationsTab = "Alerts" | "Chats" | "Comments";

export interface IBuilderProps {
  goBackRoute: string | null;
  linked: boolean;
  isLoading: boolean;
  builderData: IResumeBuild[];
  setCurrentTab: (tab: StudentResumeTab) => void;
};

export interface ICoverLetterProps {
  goBackRoute: string | null;
  linked: boolean;
  isLoading: boolean;
  coverLetterData: ICoverLetter[];
  setCurrentTab: (tab: StudentCoverLetterTab) => void;
};

export interface IScannerTableFields {
  id: number;
  resumeTitle: string;
  jobTitle: string;
  date: string;
  results: number;
  reviewedBy: string;
}

export enum StudentPROptionsCategoryGroup {
  ExpressEntry = 'express-entry',
  OtherPR = 'other-pr',
  Temporary = 'temporary',
}

export enum StudentPROptionsCategoryGroupLabel {
  ExpressEntry = 'Express Entry',
  OtherPR = 'Other PR',
  Temporary = 'Temporary',
}

export interface IProgramsDropdownOption {
  value: StudentPROptionsCategoryGroup;
  label: StudentPROptionsCategoryGroupLabel;
  difficulty?: 'easy' | 'medium' | 'hard';
}

export type TScenario = "single" | "married";

export interface IScenario {
  id: number;
  scenario: TScenario;
};

export interface IApplicantScore {
  id: number;
  applicant_id: number;
  scenario_id: number;
  score_id: number;
  score: number;
  scenario: IScenario;
};

export interface ICombinedApplicantScores {
  main: IApplicantScore[];
  spouse: IApplicantScore[];
};

export interface ITag {
  id: number;
  name: string;
};

export interface IProgramTag {
  id: number;
  program_id: number;
  tag_id: number;
  tag: ITag;
}

export interface IApplicantProgramResult {
  id: number;
  applicant_program_id: number;
  scenario_id: number;
  effective_datetime: string;
  draw_date: string;
  draw_points: number;
  qualified: boolean;
  scenario: IScenario;
}

export interface IApplicantProgram {
  id: number;
  applicant_id: number;
  program_id: number;
  program_qualified: boolean;
  favorite: boolean;
  name: string;
  description: string;
  program_tags: IProgramTag[];
  gov_url: string;
  results: IApplicantProgramResult[];
};

export interface ICombinedApplicantPrograms {
  main: IApplicantProgram[];
  spouse: IApplicantProgram[];
  main_program_filters: ITag[];
  spouse_program_filters: ITag[];
};

export interface IGroupScoreHypo {
  id: number;
  score_hypo_group_id: number;
  difficulty: number;
  description: string;
};

export interface IApplicantScoreHypoGroup {
  id: number;
  applicant_id: number;
  score: number;
  difficulty: number;
  hash: string;
  favorite: boolean;
  tag: ITag[];
  grouped_score_hypos: IGroupScoreHypo[];
};

export interface ICombinedApplicantScoreHypoGroups {
  main: IApplicantScoreHypoGroup[];
  spouse: IApplicantScoreHypoGroup[];
  main_filters: ITag[];
  spouse_filters: ITag[];
};